import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../../store/actions/usersActions';
import { fetchDashboards } from "../../../store/actions/dashsActions";
import { saveGroup, fetchGroupById } from "../../../store/actions/groupsActions";
import TitlePage from '../../../components/general/TitlePage/TitlePage';
import Input from '../../../components/general/Input/Input';
import TitleSectionComponent from '../../../components/intern/TitleSectionComponent/TitleSectionComponent';
import Button from '../../../components/general/Button/Button';
import TableUserForm from '../../../components/intern/TableInform/TableUserForm';
import TableDashboardForm from '../../../components/intern/TableInform/TableDashboardForm';
import AlertComponent from '../../../components/intern/Alert/Alert';
import './UserSearchList.css';

const CriarGrupoPage = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const { users, loadingUsers, errorUsers } = useSelector((state) => state.users);
    const { dashs, loadingDashs, errorDashs } = useSelector((state) => state.dashs);
    const { group } = useSelector((state) => state.groups);
    const { groups } = useSelector((state) => state.groups); // Acesso aos grupos no Redux
    const [dashboardName, setDashboardName] = useState("");
    const [filteredDashboards, setFilteredDashboards] = useState([]);
    const [selectedDashboards, setSelectedDashboards] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [groupNameError, setGroupNameError] = useState("");
    const [userEmail, setUserEmail] = useState("");  // Campo de e-mail de usuário
    const [selectedUsers, setSelectedUsers] = useState([]);  // Usuários selecionados
    const [filteredUsers, setFilteredUsers] = useState([]);  // Usuários filtrados pela busca
    const [alert, setAlert] = useState(null);
    const alertTimeoutId = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        return () => {
            if (alertTimeoutId.current) clearTimeout(alertTimeoutId.current);
        };
    }, []);

    useEffect(() => {
        dispatch(fetchUsers());
        dispatch(fetchDashboards());

        if (id) {
            dispatch(fetchGroupById(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (group && id) {
            setGroupName(prev => prev || group.groupName || "");
            setSelectedDashboards(prev => prev.length ? prev : (group.selectedDashboards || []));
            setSelectedUsers(prev => prev.length ? prev : (group.selectedUsers || []));
        }
    }, [group, id]);

    const showAlert = (type, message) => {
        // Limpa timeout anterior
        if (alertTimeoutId.current) {
            clearTimeout(alertTimeoutId.current);
        }
        setAlert({ type, message });

        alertTimeoutId.current = setTimeout(() => {
            setAlert(null);
            alertTimeoutId.current = null;
        }, 5000);
    };

    const handleGroupNameChange = (e) => {
        const value = e.target.value;
        setGroupName(value);

        const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s'-]+$/;

        if (value.length < 3) {
            setGroupNameError("O nome do grupo precisa ter no mínimo 3 caracteres.");
        } else if (!nameRegex.test(value)) {
            setGroupNameError("Nome do grupo inválido (use apenas letras, números e espaços).");
        } else if (groups.some(group => group.groupName.toLowerCase() === value.toLowerCase() && group.id !== id)) {
            setGroupNameError("Já existe um grupo com esse nome.");
        } else {
            setGroupNameError("");
        }
    };


    const handleDashboardNameChange = (e) => {
        const value = e.target.value;
        setDashboardName(value);

        if (value.length > 0) {
            const matches = dashs.filter(
                (dashboard) =>
                    dashboard.dashboardName.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredDashboards(matches);
        } else {
            setFilteredDashboards([]);
        }
    };

    const handleAddDashboard = (dashboard) => {
        if (!selectedDashboards.some((dash) => dash.id === dashboard.id)) {
            setSelectedDashboards([...selectedDashboards, dashboard]);
            setDashboardName("");
            setFilteredDashboards([]);
        } else {
            showAlert('excluir', 'Dashboard já adicionado!');
        }
    };

    const handleRemoveDashboard = (dashboardId) => {
        setSelectedDashboards(selectedDashboards.filter((dash) => dash.id !== dashboardId));
        showAlert('excluir', 'Dashboard removido com sucesso.');
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setUserEmail(value);

        if (value.length > 0) {
            const matches = users.filter(
                (user) => user.userEmail.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredUsers(matches);
        } else {
            setFilteredUsers([]);
        }
    };

    const handleAddUser = (email) => {
        const user = users.find((u) => u.userEmail === email);
        if (user && !selectedUsers.some((u) => u.id === user.id)) {
            setSelectedUsers([...selectedUsers, user]);
            setUserEmail("");
            setFilteredUsers([]);
        } else {
            showAlert('excluir', 'Usuário já adicionado!');
        }
    };

    const handleRemoveUser = (userId) => {
        setSelectedUsers(selectedUsers.filter((user) => user.id !== userId));
        showAlert('excluir', 'Usuário removido com sucesso.');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (groupNameError || !groupName) {
            showAlert('erro', 'Preencha todos os campos obrigatórios!');
            return;
        }

        try {
            let response;
            response = await dispatch(
                saveGroup({ groupName, selectedDashboards, selectedUsers })
            ).unwrap();
            showAlert('adicionar');
            navigate("/dashboard");
        } catch (error) {
            console.error("Erro ao salvar grupo:", error);
        }
    };

    if (loadingUsers || loadingDashs) return <p>Carregando dados...</p>;
    if (errorUsers || errorDashs) return <p>Erro: {errorUsers || errorDashs}</p>;

    return (
        <section className="container-extern content-with-sidebar">
            <TitlePage
                title="Novo Grupo"
                subtitle="Configure um novo grupo e atribua as permissões adequadas para o gerenciamento de acessos com eficiência e segurança."
            />

            <Helmet>
                <title>New Group</title>
            </Helmet>

            <form onSubmit={handleSubmit}>
                <Input
                    type="text"
                    placeholder="Nome do grupo..."
                    value={groupName}
                    onChange={handleGroupNameChange}
                    label="Nome:"
                    errorMessage={groupNameError}
                />

                <TitleSectionComponent>Permitir acesso aos dashboards existentes:</TitleSectionComponent>
                <Input
                    type="text"
                    placeholder="Digite aqui nome do dashboard cadastrado"
                    label="Nome do Dashboard:"
                    value={dashboardName}
                    onChange={handleDashboardNameChange}
                />

                {filteredDashboards.length > 0 ? (
                    <ul className="user-search-list common-list-style">
                        {filteredDashboards.map((dash) => (
                            <li
                                key={dash.id || dash.dashboardName}
                                onClick={() => handleAddDashboard(dash)}
                                className="user-search-item"
                            >
                                {dash.dashboardName}
                            </li>
                        ))}
                    </ul>
                ) : (
                    dashboardName && <p className="no-results common-list-style">Nenhum dashboard encontrado com esse nome. Tente novamente.</p>
                )}

                <TableDashboardForm
                    dados={selectedDashboards}
                    tipoContato="Dashboard"
                    exibirAcesso={false}
                    exibirEditar={false}
                    onExcluir={dash => handleRemoveDashboard(dash.id)}
                    onEditar={() => { }}
                />

                <TitleSectionComponent>Permitir acesso aos usuários existentes:</TitleSectionComponent>
                <Input
                    type="email"
                    placeholder="E-mail de usuário..."
                    value={userEmail}
                    onChange={handleEmailChange}
                    label="E-mail do Usuário:"
                />

                {filteredUsers.length > 0 ? (
                    <ul className="user-search-list common-list-style">
                        {filteredUsers.map((user) => (
                            <li
                                key={user.id}
                                onClick={() => handleAddUser(user.userEmail)}
                                className="user-search-item"
                            >
                                {user.userEmail}
                            </li>
                        ))}
                    </ul>
                ) : (
                    userEmail && <p className="no-results common-list-style">Nenhum usuário encontrado com esse e-mail. Tente novamente.</p>
                )}

                <TableUserForm
                    dados={selectedUsers}
                    tipoContato="email"
                    exibirAcesso={false}
                    exibirEditar={false}
                    onExcluir={user => handleRemoveUser(user.id)}
                    onEditar={() => { }}
                />

                <div className='section-btn-page'>
                    <Button type="submit" variant="add" className='btn-add'
                        disabled={!!groupNameError || !groupName}
                    >
                        Criar Grupo
                    </Button>
                </div>

                {alert && <AlertComponent type={alert.type} onClose={() => setAlert(null)} />}
            </form>
        </section>
    );
};

export default CriarGrupoPage;
